import { useState } from 'react'
import { useEstimationPreview } from './Dialog.h'
import { useEstimation } from '@shared/hooks/estimation'
import { usePermission } from '@shared/hooks/permissions'
import {
    useTheme,
    useMediaQuery
} from '@mui/material'

import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import DialogActions from '@mui/material/DialogActions'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Divider from '@mui/material/Divider'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import MotionDialog from '@shared/components/MotionDialog'
import workTypes from '@shared/utils/workTypes'
import Link from '@mui/material/Link'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import List from '@mui/material/List'
import Property from './Property'
import ImageGallary from './ImageGallary'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import 'yet-another-react-lightbox/styles.css'
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp'
import ConsultantItem from '@shared/components/ConsultantItem'
import since from '@shared/utils/since'

export default function PreviewEstimation({instance}) {
    const [isSignatureLoaded, setIsSignatureLoaded] = useState(false)

    const {
        tab,
        setTab,

        anchor,
        setAnchor,
        exportAnchor,
        setExportAnchor,

        handleClose,
        documentsImages,
        locationImages,
        violationsImages,
        lightbox,
        currentSlides,
        openLightbox,
        closeLightbox
    } = useEstimationPreview(instance)
    
    const {
        handleEdit,
        handleDelete,
        handleExport,
        handlePin,
    } = useEstimation(instance)
    
    const { hasPermission } = usePermission()
    const theme = useTheme()
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

    return (
        <MotionDialog
            onClose={handleClose}
            slotProps={{
                paper: {
                    sx: {height: '100%'}
                }
            }}
            fullScreen={isMobile}
        >
            <DialogTitle>مقايسة</DialogTitle>
            <Tabs
                value={tab}
                onChange={(e, v) => setTab(v)}
                variant='scrollable'
            >
                <Tab value='data' label='البيانات'/>
                {documentsImages.length > 0 &&
                    <Tab value='documents' label={`النماذج (${documentsImages.length})`}/>}
                {locationImages.length > 0 &&
                    <Tab value='location' label={`الموقع (${locationImages.length})`}/>}
                {violationsImages.length > 0 &&
                    <Tab value='violations' label={`مخالفات السلامة (${violationsImages.length})`}/>}
                {instance.attachments.length > 0 &&
                    <Tab value='attachments' label={`المرفقات (${instance.attachments.length})`}/>}
            </Tabs>
            <Divider />
            <DialogContent>
                <Stack
                    spacing={2}
                    sx={{display: tab == 'data' ? 'flex' : 'none'}}
                >
                    <Typography variant='caption'>البيانات الأساسية</Typography>
                    <Property
                        label='رقم المقايسة'
                        value={instance.number}
                    />
                    <Property
                        label='المقاول'
                        value={instance.contractor}
                    />
                    <Property
                        label='نوع العمل'
                        value={workTypes[instance.work_type]}
                    />
                    {instance.work_type == 'assets' &&
                        <>
                            <Property
                                label='رقم المُعدّة'
                                value={instance.equipment_no}
                            />
                            <Property
                                label='نوع الموقع'
                                value={instance.site_type}
                            />
                        </>
                    }
                    <Property
                        label='المكتب'
                        value={instance.office_name}
                    />
                    <Property
                        label='الحي'
                        value={instance.district}
                    />
                    <Property
                        label='الحالة'
                        value={instance.is_draft ? 'مؤرشفة' : 'منجزة'}
                    />
                    <Property
                        label='تاريخ الإنشاء'
                        value={since(instance.created_at)}
                    />
                    <Divider />
                    <Typography variant='caption'>البيانات الإختيارية</Typography>
                    <Property
                        label='اسم المشرف'
                        value={instance.supervisor_name}
                    />
                    <Property
                        label='توقيع المشرف'
                        value={
                            !instance.supervisor_signature
                                ? ''
                                : (
                                    <>
                                        {!isSignatureLoaded && (
                                            <Skeleton 
                                                variant="rectangular" 
                                                width="100%" 
                                                height={150} 
                                                sx={{ borderRadius: 1 }} 
                                            />
                                        )}
                                        <img
                                            src={instance.supervisor_signature}
                                            onLoad={() => setIsSignatureLoaded(true)}
                                            style={{
                                                width: '100%',
                                                border: '1px solid #777',
                                                borderRadius: 5,
                                                maxHeight: '300px',
                                                display: isSignatureLoaded ? 'block' : 'none'
                                            }}
                                        />
                                    </>
                                )
                        }
                    />
                    <Property
                        label='ممثل الـ PDC'
                        value={instance.pdc_delegate_name}
                    />
                    <Property
                        label='رقم المحطة'
                        value={instance.station_no}
                    />
                    <Property
                        label='وصف العمل'
                        value={instance.notes}
                        vertical={instance.notes != ''}
                    />
                    <Divider />
                    <Typography variant='caption'>المهندسون المشاركون</Typography>
                    <List>
                        {instance.active_users?.map(user => (
                            <ConsultantItem
                                key={user.id}
                                id={user.id}
                                name={user.name}
                                lastActivity={user.last_activity}
                                disableGutters
                            />
                        ))}
                    </List>
                </Stack>
                <div style={{ display: tab == 'documents' ? 'block' : 'none' }}>
                    <ImageGallary
                        images={documentsImages}
                        onImageClick={openLightbox('documents')}
                    />
                </div>

                <div style={{ display: tab == 'location' ? 'block' : 'none' }}>
                    <ImageGallary
                        images={locationImages}
                        onImageClick={openLightbox('location')}
                    />
                </div>

                <div style={{ display: tab == 'violations' ? 'block' : 'none' }}>
                    <ImageGallary
                        images={violationsImages}
                        onImageClick={openLightbox('violations')}
                    />
                </div>
                <div style={{display: tab == 'attachments' ? 'block' : 'none'}}>
                    {instance.attachments.map((atch, i) =>
                        <Box
                            key={atch.id}
                            sx={{
                                width: '100%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                py: 1
                            }}
                        >
                            <Typography>{i+1}</Typography>
                            <Link
                                target='_blank'
                                href={atch.file}
                                sx={{width: '100%'}}
                            >
                                {atch.file.split('/').slice(-1)}
                            </Link>
                        </Box>
                    )}
                </div>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='text'
                    onClick={handleClose}
                >إغلاق</Button>
                <Button
                    variant='text'
                    onClick={e => setAnchor(e.currentTarget)}
                >
                    الإجراءات
                    <ArrowDropUpIcon />
                </Button>
                <Menu
                    open={!!anchor}
                    anchorEl={anchor}
                    onClose={() => setAnchor(null)}
                    anchorOrigin={{
                        horizontal: 'left'
                    }}
                    transformOrigin={{
                        vertical: 'bottom',
                        horizontal: 'left'
                    }}
                >
                    {hasPermission('change_estimation') &&
                        <MenuItem onClick={() => {
                            handleEdit()
                            setAnchor(null)
                        }}>تعديل</MenuItem>
                    }
                    {hasPermission('delete_estimation') &&
                        <MenuItem onClick={() => {
                            handleDelete()
                            setAnchor(null)
                        }}>حذف</MenuItem>
                    }
                    <MenuItem onClick={e => setExportAnchor(e.currentTarget)}>تصدير</MenuItem>
                    <MenuItem onClick={() => {
                        handlePin()
                        setAnchor(null)
                    }}>تثبيت</MenuItem>
                </Menu>
                <Menu
                    open={!!exportAnchor}
                    onClose={() => setExportAnchor(null)}
                    anchorEl={exportAnchor}
                    anchorOrigin={{
                        horizontal: 'right'
                    }}
                    transformOrigin={{
                        vertical: 'top',
                        horizontal: 'left'
                    }}
                >
                    <MenuItem
                        onClick={() => {
                            setAnchor(null)
                            setExportAnchor(null)
                            handleExport()
                        }}
                    >تصدير الكل</MenuItem>
                    <MenuItem
                        onClick={() => {
                            setAnchor(null)
                            setExportAnchor(null)
                            handleExport(['documents'])
                        }}
                    >النماذج فقط</MenuItem>
                    <MenuItem
                        onClick={() => {
                            setAnchor(null)
                            setExportAnchor(null)
                            handleExport(['location', 'violations'])
                        }}
                    >الصور فقط</MenuItem>
                </Menu>
            </DialogActions>
            <Lightbox
                open={lightbox.open}
                close={closeLightbox}
                index={lightbox.index}
                slides={currentSlides}
                plugins={[Zoom]}
                carousel={{ finite: true }}
            />
        </MotionDialog>
    )
}